import { Dialog } from '@headlessui/react';
import clsx from 'clsx';
import { AnimatePresence, HTMLMotionProps, Variants, motion } from 'framer-motion';
import { PropsWithChildren } from 'react';
import { IoClose } from 'react-icons/io5';
import { useMediaQuery } from 'react-responsive';
import { useSwipeable } from 'react-swipeable';
import { ModalProps } from './useModal';

const variants: Variants = {
  open: {
    opacity: 1,
    transform: 'translateY(0px)'
  },
  closed: {
    opacity: 0,
    transform: 'translateY(40px)'
  },
  exit: {
    opacity: 0,
    transform: 'translateY(100%)'
  },
  'md:exit': {
    opacity: 0
  }
};

const overlayVariants: Variants = {
  open: {
    opacity: 1,
    transition: {
      duration: 0.2
    }
  },
  closed: {
    opacity: 0
  }
};

export function Modal<T = void>({
  children,
  className,
  close,
  isOpen,
  ...props
}: PropsWithChildren<ModalProps<T> & HTMLMotionProps<'div'>>) {
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

  // Weird bit- we need to remove the open function from the props.
  // It's not used internally by the modal component, and it upsets headlessreact.
  const newProps = { ...props } as any;
  delete newProps.open;

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <Dialog
          className="text-primary-std pointer-events-none fixed inset-0 z-50 flex h-screen w-screen md:items-center md:justify-center"
          static={true}
          open={true}
          onClose={close}
        >
          <Dialog.Overlay
            as={motion.div}
            variants={overlayVariants}
            initial="closed"
            animate="open"
            exit="closed"
            key="modal:overlay"
            className="pointer-events-auto fixed inset-0 bg-black/30"
          />
          <Dialog.Panel
            {...newProps}
            className={clsx(
              'z-50 flex h-fit w-full flex-col place-self-end md:place-self-center',
              'shadow-2xl',
              'pointer-events-auto',
              className
            )}
            key="modal:panel"
            as={motion.div}
            variants={variants}
            initial="closed"
            animate="open"
            exit={isMobile ? 'exit' : 'md:exit'}
          >
            {children}
          </Dialog.Panel>
        </Dialog>
      )}
    </AnimatePresence>
  );
}

Modal.Controls = function Controls<T = void>({ close, children }: PropsWithChildren<ModalProps<T>>) {
  const bind = useSwipeable({
    onSwipedDown: close
  });
  return (
    <header className="sticky top-0 flex items-center justify-center pt-2 md:pt-0" {...bind}>
      <div className="flex-1">{children}</div>
      <button onClick={close}>
        <IoClose className="h-6 w-6" />
      </button>
      <div className="absolute -top-4 right-1/2 h-2 w-10 translate-x-1/2 rounded-full bg-gray-300 md:hidden" />
    </header>
  );
};
